/* Display compatible variants together without merging Shopify's underlying lines. */
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const offerProperty = '_luff_offer';
  let queue = Promise.resolve();

  function identity(item) {
    // Preserve all merchant/app properties except our own reward-origin marker.
    const properties = Object.entries(item.properties || {})
      .filter(([key, value]) => key !== offerProperty && value != null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b));
    const nested = item.parent_relationship || item.item_components?.length;
    if (nested) return item.key;
    return 'group:' + encodeURIComponent(JSON.stringify([
      item.variant_id || item.id, properties,
      item.selling_plan_allocation?.selling_plan?.id || null,
      nested ? item.key : null
    ]));
  }

  function groupItems(items, attributes = {}) {
    let rewards = {};
    try { rewards = JSON.parse(attributes._luff_offer_earned || '{}').earned || {}; } catch (_) { /* No offer ledger. */ }
    const groups = new Map();
    for (const item of items) {
      const key = identity(item);
      if (!groups.has(key)) groups.set(key, {
        ...item, key, lines: [], quantity: 0, line_price: 0,
        final_line_price: 0, original_line_price: 0, compare_at_line_price: 0,
        offerAdded: false, freeQuantity: 0
      });
      const group = groups.get(key);
      group.lines.push(item);
      group.quantity += item.quantity;
      const finalPrice = Number(item.final_line_price ?? item.line_price ?? 0);
      group.line_price += finalPrice;
      group.final_line_price += finalPrice;
      group.original_line_price += Number(item.original_line_price ?? item.line_price ?? 0);
      group.compare_at_line_price += Number(item.compare_at_line_price ??
        ((item.compare_at_price || item.variant?.compare_at_price || 0) * item.quantity));
      group.offerAdded ||= Boolean(item.properties?.[offerProperty]) || Number(rewards[item.variant_id] || 0) > 0;
      // Count only zero-priced units actually returned by Shopify.
      if (finalPrice === 0) group.freeQuantity += item.quantity;
    }
    // Keep ordinary/qualifying products in their order, then their automatic rewards.
    return [...groups.values()].sort((a, b) => Number(a.offerAdded) - Number(b.offerAdded));
  }

  async function readCart() {
    const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh your cart. Please try again.');
    return response.json();
  }

  async function mutate(path, body) {
    const response = await fetch(`${root}cart/${path}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Could not update your cart.');
    return data;
  }

  async function changeGroup(key, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Enter a whole quantity of zero or more.');
    let current = await readCart();
    let group = groupItems(current.items).find(item => item.key === key);
    if (!group) throw new Error('Your cart changed. Refresh it and try again.');
    if (quantity > group.quantity) {
      const line = group.lines.find(item => !item.properties?.[offerProperty]) || group.lines[0];
      const properties = { ...line.properties };
      delete properties[offerProperty];
      const body = { id: line.variant_id, quantity: quantity - group.quantity, properties };
      const plan = line.selling_plan_allocation?.selling_plan?.id;
      if (plan) body.selling_plan = plan;
      await mutate('add', body);
    } else {
      // A discount can change every line key after each mutation. Match the group
      // again against a fresh cart rather than reusing any old line keys.
      while (group && group.quantity > quantity) {
        const line = group.lines.find(item => !item.properties?.[offerProperty]) || group.lines[0];
        const reduction = Math.min(line.quantity, group.quantity - quantity);
        await mutate('change', { id: line.key, quantity: line.quantity - reduction });
        const before = group.quantity;
        current = await readCart();
        group = groupItems(current.items).find(item => item.key === key);
        if (group && group.quantity >= before) throw new Error('Your cart changed. Refresh it and try again.');
      }
    }
    if (window.LuffCartOffers) await window.LuffCartOffers.reconcile();
    current = await readCart();
    window.VanillaCartState?.updateCart(current);
    return current;
  }

  function enqueue(task) {
    const operation = queue.then(task).catch(async error => {
      // A multi-line operation may partially succeed. Restore the actual cart
      // instead of leaving an optimistic quantity or stale line key on screen.
      try { window.VanillaCartState?.updateCart(await readCart()); } catch (_) { /* Keep original error. */ }
      throw error;
    });
    queue = operation.catch(() => {});
    return operation;
  }

  function update(key, quantity) {
    return enqueue(() => changeGroup(key, quantity));
  }

  function addonKey(variantId) {
    return identity({ variant_id: Number(variantId), properties: {} });
  }

  function setAddon(variantId, enabled) {
    return enqueue(async () => {
      const key = addonKey(variantId);
      let current = await readCart();
      const group = groupItems(current.items).find(item => item.key === key);
      if (!enabled && group) return changeGroup(key, 0);
      if (enabled && !group) {
        await mutate('add', { id: Number(variantId), quantity: 1 });
        if (window.LuffCartOffers) await window.LuffCartOffers.reconcile();
        current = await readCart();
      }
      window.VanillaCartState?.updateCart(current);
      return current;
    });
  }

  function priceSummary(item) {
    if (!item.offerAdded) return '';
    if (item.freeQuantity === item.quantity) return `${item.quantity} free with your qualifying products`;
    if (item.freeQuantity > 0) return `${item.freeQuantity} free · ${item.quantity - item.freeQuantity} paid`;
    return 'Current price includes applicable discounts';
  }

  function groupCartPage() {
    const container = document.querySelector('[data-luff-cart-groups]');
    const data = document.querySelector('[data-luff-cart-data]');
    if (!container || !data || container.dataset.grouped) return;
    const cart = JSON.parse(data.textContent);
    const rows = new Map(Array.from(container.querySelectorAll('[data-cart-source-key]'))
      .map(row => [row.dataset.cartSourceKey, row]));
    cart.items.forEach(item => { item.compare_at_line_price = Number(rows.get(item.key)?.dataset.compareAtLinePrice || 0); });
    const money = amount => window.Shopify.formatMoney(amount, window.money_format);
    for (const group of groupItems(cart.items, cart.attributes)) {
      const row = rows.get(group.lines[0].key);
      if (!row) continue;
      row.dataset.initQty = group.quantity;
      row.querySelectorAll('[data-line-key]').forEach(control => { control.dataset.lineKey = group.key; });
      const input = row.querySelector('input[name="updates[]"]');
      if (input) {
        input.value = group.quantity;
        // Grouped quantities are sent through Ajax by key. Do not submit this
        // display aggregate as a positional updates[] value for a raw line.
        input.setAttribute('form', 'luff-cart-display-quantities');
      }
      row.querySelectorAll('[data-render-self-price], .line-item_final-price').forEach(price => { price.innerHTML = money(group.final_line_price); });
      const oldPrice = row.querySelector('.line-item_head-price s');
      if (oldPrice) oldPrice.innerHTML = money(group.compare_at_line_price || group.original_line_price);
      const detail = document.createElement('p');
      detail.className = 'luff-cart-offer-summary';
      detail.textContent = priceSummary(group);
      if (detail.textContent) row.querySelector('.line-item_infos-details')?.appendChild(detail);
      for (const line of group.lines.slice(1)) rows.get(line.key)?.remove();
      container.appendChild(row);
    }
    container.dataset.grouped = 'true';
  }

  window.LuffCartGroups = { identity, groupItems, update, addonKey, setAddon, priceSummary, groupCartPage, settle: () => queue };
  document.addEventListener('DOMContentLoaded', groupCartPage);
})();
